import React, { useEffect, useRef, useState } from "react";
import { Plus, Trash2, Upload, RotateCcw, Save } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { GROUPS, DEFAULTS, DEFAULT_REVIEWS, ABOUT_PAGES, BLOCK_PAGES, applyContent, cacheContent } from "../../lib/content";
import SEO_CONTENT from "../../seo-content.json";

const box = "bg-white rounded-2xl border border-[#183C35]/10 p-4 sm:p-6";
const h = "font-serif-d text-[20px] text-[#183C35]";
const inputCls = "rounded-xl text-[16px] sm:text-[14.5px]";

const ImageField = ({ value, onChange, testid }) => {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const upload = async (f) => {
    if (!f) return;
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", f);
      const { data } = await api.post("/admin/media", fd, { timeout: 120000 });
      onChange(data.url); toast.success("Photo uploaded – press Save to publish it");
    } catch (e) { toast.error(e?.response?.data?.detail || "Upload failed"); } finally { setBusy(false); if (ref.current) ref.current.value = ""; }
  };
  return (
    <div className="flex items-center gap-3">
      <img src={value} alt="" className="w-24 h-16 rounded-lg object-cover border border-[#183C35]/10 bg-[#F7F3EA]" />
      <input ref={ref} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={(e) => upload(e.target.files?.[0])} />
      <button type="button" disabled={busy} onClick={() => ref.current?.click()} className="inline-flex items-center gap-1.5 rounded-full border border-[#183C35]/15 px-3.5 py-2 text-[13px] text-[#183C35] hover:bg-[#F6F2E9] disabled:opacity-60" data-testid={testid}>
        <Upload className="w-3.5 h-3.5" />{busy ? "Uploading…" : "Upload new photo"}
      </button>
    </div>
  );
};

const Field = ({ k, label, type, data, set }) => {
  const val = data[k] ?? DEFAULTS[k] ?? "";
  const changed = data[k] !== undefined && data[k] !== DEFAULTS[k];
  const update = (v) => set({ ...data, [k]: v });
  const reset = () => { const n = { ...data }; delete n[k]; set(n); };
  const tid = `content-${k.replace(/\./g, "-")}`;
  return (
    <div className="py-3 border-b border-[#183C35]/5 last:border-0">
      <div className="flex items-center justify-between gap-2 mb-1.5">
        <label className="text-[13px] font-medium text-[#202522]/75">{label}</label>
        {changed && <button type="button" onClick={reset} className="inline-flex items-center gap-1 text-[12px] text-[#3E7F8E]"><RotateCcw className="w-3 h-3" />Reset</button>}
      </div>
      {type === "image" ? <ImageField value={val} onChange={update} testid={tid} />
        : type === "textarea" ? <Textarea value={val} onChange={(e) => update(e.target.value)} rows={3} className={inputCls} data-testid={tid} />
          : <Input value={val} onChange={(e) => update(e.target.value)} className={`h-11 ${inputCls}`} data-testid={tid} />}
    </div>
  );
};

const ListEditor = ({ items, onChange, fields, addLabel, testid }) => (
  <div className="space-y-3" data-testid={testid}>
    {items.map((it, i) => (
      <div key={i} className="rounded-xl border border-[#183C35]/10 p-3 sm:p-4 bg-[#FBF9F4] space-y-2">
        {fields.map(([f, ph, area]) => area
          ? <Textarea key={f} value={it[f] || ""} placeholder={ph} rows={3} onChange={(e) => onChange(items.map((x, j) => (j === i ? { ...x, [f]: e.target.value } : x)))} className={`bg-white ${inputCls}`} />
          : <Input key={f} value={it[f] || ""} placeholder={ph} onChange={(e) => onChange(items.map((x, j) => (j === i ? { ...x, [f]: e.target.value } : x)))} className={`h-11 bg-white ${inputCls}`} />)}
        <button type="button" onClick={() => onChange(items.filter((_, j) => j !== i))} className="inline-flex items-center gap-1 text-[12.5px] text-[#B42318]/80 hover:text-[#B42318]" data-testid={`${testid}-delete-${i}`}><Trash2 className="w-3.5 h-3.5" />Delete</button>
      </div>
    ))}
    <button type="button" onClick={() => onChange([...items, {}])} className="inline-flex items-center gap-1.5 rounded-full border border-dashed border-[#183C35]/30 px-4 py-2 text-[13px] text-[#183C35] hover:bg-[#F6F2E9]" data-testid={`${testid}-add`}><Plus className="w-3.5 h-3.5" />{addLabel}</button>
  </div>
);

export const ContentEditor = () => {
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(false);
  const [blockPage, setBlockPage] = useState("home");
  useEffect(() => { api.get("/content").then((r) => setData(r.data)).catch(() => toast.error("Could not load website texts")); }, []);
  if (!data) return <p className="py-10 text-center text-[#202522]/55">Loading…</p>;
  const save = async () => {
    setSaving(true);
    try {
      const { data: saved } = await api.put("/admin/content", { data });
      setData(saved); applyContent(saved); cacheContent(saved);
      toast.success("Saved – your website is updated");
    } catch { toast.error("Could not save"); } finally { setSaving(false); }
  };
  const reviews = data.reviews || DEFAULT_REVIEWS;
  const blocks = data.blocks || {};
  const about = data.about || {};
  return (
    <div className="space-y-5 pb-24" data-testid="content-editor">
      <p className="text-[13.5px] text-[#202522]/65">Change any text or photo below, then press <strong>Save changes</strong>. Changes appear on the website straight away. Photos are saved with descriptions Google can read.</p>
      {GROUPS.map(([g, fields]) => (
        <details key={g} className={box} data-testid={`content-group-${g.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
          <summary className={`${h} cursor-pointer select-none`}>{g}</summary>
          <div className="mt-2">{fields.map(([k, label, type]) => <Field key={k} k={k} label={label} type={type} data={data} set={setData} />)}</div>
        </details>
      ))}
      <details className={box} data-testid="content-group-reviews">
        <summary className={`${h} cursor-pointer select-none`}>Reviews ({reviews.length})</summary>
        <p className="mt-2 mb-3 text-[13px] text-[#202522]/60">Copy your real Google reviews here: the guest's name, country and their words.</p>
        <ListEditor items={reviews} onChange={(v) => setData({ ...data, reviews: v })} addLabel="Add review" testid="reviews-editor"
          fields={[["name", "Name, e.g. Sarah M."], ["country", "Country, e.g. United Kingdom"], ["quote", "Review text", true]]} />
      </details>
      <details className={box} data-testid="content-group-about">
        <summary className={`${h} cursor-pointer select-none`}>"About this service" texts</summary>
        <div className="mt-2">{ABOUT_PAGES.map(([p, l]) => (
          <div key={p} className="py-3 border-b border-[#183C35]/5 last:border-0">
            <label className="text-[13px] font-medium text-[#202522]/75">{l}</label>
            <Textarea rows={4} value={about[p] ?? SEO_CONTENT.about[p]} onChange={(e) => setData({ ...data, about: { ...about, [p]: e.target.value } })} className={`mt-1.5 ${inputCls}`} data-testid={`about-${p.replace(/\//g, "-")}`} />
          </div>
        ))}</div>
      </details>
      <details className={box} open data-testid="content-group-blocks">
        <summary className={`${h} cursor-pointer select-none`}>Add your own text to a page</summary>
        <div className="mt-3 flex flex-wrap gap-2">
          {BLOCK_PAGES.map(([k, l]) => <button type="button" key={k} onClick={() => setBlockPage(k)} className={`rounded-full px-3.5 py-1.5 text-[13px] ${blockPage === k ? "bg-[#183C35] text-[#F7F3EA]" : "bg-white border border-[#183C35]/15 text-[#183C35]"}`} data-testid={`blocks-page-${k}`}>{l}{(blocks[k] || []).length ? ` (${blocks[k].length})` : ""}</button>)}
        </div>
        <div className="mt-4">
          <ListEditor items={blocks[blockPage] || []} onChange={(v) => setData({ ...data, blocks: { ...blocks, [blockPage]: v } })} addLabel="Add text section" testid="blocks-editor"
            fields={[["heading", "Heading (optional)"], ["text", "Text", true]]} />
        </div>
      </details>
      <div className="fixed bottom-0 inset-x-0 z-40 bg-white/95 backdrop-blur border-t border-[#183C35]/10 px-4 py-3 flex justify-end">
        <button onClick={save} disabled={saving} className="inline-flex items-center gap-2 rounded-full bg-[#183C35] text-[#F7F3EA] px-7 py-3 font-medium hover:bg-[#21524A] disabled:opacity-60" data-testid="content-save-btn"><Save className="w-4 h-4" />{saving ? "Saving…" : "Save changes"}</button>
      </div>
    </div>
  );
};
